import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { ClientId } from "../../domain/types";
import { CO_BASE } from "../../domain/steps";
import { persistAnswer, useCoAnswer, useCoClient } from "../../data/store";
import { fetchOutstandingImport, type OutstandingKind } from "../../data/outstandingApi";
import { dbTrue, fetchZohoRecords, type ZohoBankAccountDTO } from "../../data/zohoRecordsApi";
import { Callout } from "../../components/Callout";
import { DecisionCards } from "../../components/DecisionCards";
import { SubStepShell } from "../../components/SubStepShell";
import { useSubStepNav } from "../useSubStepNav";

export const CATCH_UP_TRAINING_ANSWER_KEY = "goLive.catchUpTraining";

/**
 * The two decisions this step records. The reference design (onboarding-v2
 * and the v1 chapters) never got past its copy — "bring outstanding items
 * current, and decide whether Zournal learns from past records" — so the
 * shape here is ours: the outstanding work itself already lives on the
 * Opening balances screen (real Zoho pushes), which makes this step the
 * confirmation of that work plus the one question nothing else asks.
 *
 * The step itself only exists for books with a past (see steps.ts). For
 * everyone else Books questions writes this record: nothing outstanding,
 * start fresh, marked `defaulted` so it can be told apart from a human
 * answer if the bookkeeping type later changes back.
 */
export interface CatchUpTrainingAnswer {
  outstandingConfirmed?: boolean;
  training?: "learn" | "fresh";
  defaulted?: boolean;
}

const TRAINING_OPTIONS = [
  {
    value: "learn",
    title: "Learn from past entries",
    description: "Zournal studies how past transactions were categorised and applies the same treatment going forward.",
    consequence: "Best when the existing books are clean and consistent.",
  },
  {
    value: "fresh",
    title: "Start fresh",
    description: "Zournal categorises from its own rules only, ignoring how the old books did it.",
    consequence: "Best when past categorisation was inconsistent or the books are new.",
  },
];

/**
 * What this step could find out on its own about work still pending: an
 * outstanding-documents import left open on Opening balances, and bank
 * transactions the books hold uncategorised. Null = could not read (books
 * not connected) — different from "read and found nothing".
 */
interface PendingWork {
  openImports: OutstandingKind[];
  uncategorised: { label: string; count: number }[] | null;
}

function bankLabel(account: ZohoBankAccountDTO): string {
  return [account.bank_name ?? account.account_name ?? "Bank account", account.masked_number]
    .filter(Boolean)
    .join(" ");
}

export function CatchUpTrainingStep({ clientId }: { clientId: ClientId }) {
  const navigate = useNavigate();
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "catch-up-training");
  const answer = useCoAnswer<CatchUpTrainingAnswer>(clientId, CATCH_UP_TRAINING_ANSWER_KEY) ?? {};

  const [pending, setPending] = useState<PendingWork | null>(null);

  useEffect(() => {
    let live = true;

    async function read() {
      const openImports: OutstandingKind[] = [];
      // Each read is best-effort: a client with no books connection has no
      // imports and no bank list, and that must not block the step.
      for (const kind of ["invoice", "bill"] as OutstandingKind[]) {
        try {
          const review = await fetchOutstandingImport(clientId, kind);
          if (review.import && review.import.status !== "done" && review.import.status !== "cancelled") {
            openImports.push(kind);
          }
        } catch {
          // No import state to warn about.
        }
      }

      let uncategorised: PendingWork["uncategorised"] = null;
      try {
        const records = await fetchZohoRecords(clientId, ["bank_accounts"]);
        uncategorised = (records.bank_accounts ?? [])
          .filter((account) => dbTrue(account.is_active) && Number(account.uncategorised_count) > 0)
          .map((account) => ({ label: bankLabel(account), count: Number(account.uncategorised_count) }));
      } catch {
        // Books not readable — the confirmation below carries the weight alone.
      }

      if (live) setPending({ openImports, uncategorised });
    }

    void read();
    return () => {
      live = false;
    };
  }, [clientId]);

  if (!client) return null;

  const clientName = client.name || "this client";
  const startDate = client.accountStartDate || "the start date";
  const catchUpClient = client.bookkeepingType === "catch-up";

  function patch(changes: CatchUpTrainingAnswer) {
    // A human touched the step, so whatever is stored is no longer a default.
    const next = { ...answer, ...changes };
    delete next.defaulted;
    void persistAnswer<CatchUpTrainingAnswer>(clientId, CATCH_UP_TRAINING_ANSWER_KEY, next);
  }

  const ready = Boolean(answer.outstandingConfirmed) && answer.training !== undefined;

  return (
    <SubStepShell
      title="Catch-up & training"
      description={`Bring ${clientName}'s outstanding items current, and decide whether Zournal learns from past records.`}
      thenWhat={ready ? "Next: go live." : "Confirm the catch-up and pick a training option to continue."}
      onContinue={() => void goNext()}
      continueDisabled={!ready}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-semibold">Outstanding items</h3>
          <p className="text-sm text-muted-foreground">
            {catchUpClient
              ? `A catch-up client goes live only once the backlog is processed into the books.`
              : `Everything unpaid or unmatched as of ${startDate} should already be in the books.`}
          </p>
        </div>

        {pending === null ? (
          <p className="text-sm text-muted-foreground">Checking for unfinished work…</p>
        ) : (
          <>
            {pending.openImports.length > 0 && (
              <Callout variant="warning">
                An outstanding {pending.openImports.map((kind) => (kind === "invoice" ? "invoices" : "bills")).join(" and ")}{" "}
                import is still open — finish the push or cancel it before going live.{" "}
                <button
                  type="button"
                  className="font-medium underline underline-offset-2"
                  onClick={() => navigate(`${CO_BASE}/${clientId}/opening-balances`)}
                >
                  Go to Opening balances
                </button>
              </Callout>
            )}

            {pending.uncategorised !== null &&
              (pending.uncategorised.length > 0 ? (
                <Callout variant="warning">
                  The books still hold uncategorised bank transactions:{" "}
                  {pending.uncategorised.map((entry) => `${entry.label} (${entry.count})`).join(", ")}. These will land
                  in the first month-close as work.
                </Callout>
              ) : (
                <p className="text-sm text-muted-foreground">No uncategorised bank transactions in the books.</p>
              ))}
          </>
        )}

        <label className="flex cursor-pointer items-start gap-2 rounded-lg border p-4 text-sm">
          <input
            type="checkbox"
            checked={Boolean(answer.outstandingConfirmed)}
            onChange={(event) => patch({ outstandingConfirmed: event.target.checked })}
            className="mt-0.5 size-3.5"
          />
          <span>
            Everything outstanding as of {startDate} is in the books — invoices, bills and bank transactions are
            current.
          </span>
        </label>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-semibold">Training</h3>
          <p className="text-sm text-muted-foreground">
            Should Zournal learn from how {clientName}'s past entries were categorised?
          </p>
        </div>

        <DecisionCards
          options={TRAINING_OPTIONS}
          selected={answer.training ? [answer.training] : []}
          onToggle={(value) => patch({ training: value as CatchUpTrainingAnswer["training"] })}
          multiple={false}
        />
      </div>
    </SubStepShell>
  );
}
